import axios, { type AxiosResponse } from 'axios';

import Log from '@/Logger';
import { getAccessToken } from '@/auth';


function getEwbReqId(resp: any) {
    return resp?.headers?.['ricoh-ewb-request-id'] ?? '-';
}

const GRAPHQL_API_URL = import.meta.env.VITE_GRAPHQL_API_URL ?? '';

type Input = {
    [key: string]: Input | string | number | boolean
}

type GraphQLBody = any;

export type KintoneAppInfo = {
    kintoneAppId: string
    kintoneAppName: string
    kintoneAppDescription: string
    kintoneAppCreatedAt: string
    kintoneAppModifiedAt: string
    kintoneAppLayoutJson: string
    kintoneAppFieldInfoJson: string
}

export type LinkServiceTokenExpiredDate = {
    accessTokenExpiredDate: string,
    refreshTokenExpiredDate: string
}

export type KintoneToken = {
    accessToken: {
        token: string,
        expiredDate: string
    },
    endpointUrl: string
}

type kintoneResponse = {
    kintoneResponse: string
}

function mutation<T>(method: 'createDocumentUrl', inputType: 'CreateDocumentUrlInput', input: Input, output: string[]): Promise<AxiosResponse<T, GraphQLBody>>;
function mutation<T>(method: 'kintoneRestAPIWrapper', inputType: 'KintoneRestAPIWrapperInput', input: Input, output: string[]): Promise<AxiosResponse<T, GraphQLBody>>;

async function mutation<T>(method: string, inputType: string, input: Input, output: string[]): Promise<AxiosResponse<T, GraphQLBody>> {
    const logger = Log.getLogger();

    logger.api.info('Cognito', 'Start to request: currentSession');
    const token = await getAccessToken();
    logger.api.info('Cognito', 'Succeed to request: currentSession');

    const apiResponse: AxiosResponse<T, GraphQLBody> = await axios.post<T>(
        GRAPHQL_API_URL,
        `{"query": "mutation($input:${inputType}!) { ${method}(input:$input) { ${output.join(', ')} }}", "variables": { "input": ${JSON.stringify(input)} }}`,
        {
            headers: {
                'Content-Type': 'application/graphql',
                'Authorization': 'Bearer ' + token,
            }
        }
    );

    return apiResponse;
}

export async function createDocumentUrl(deviceId: string, documentName: string, contentType: string): Promise<{ url: string }> {
    const logger = Log.getLogger();
    logger.api.info('Lantana-API', 'Start to request: createDocumentUrl');
    const resp = await mutation<{ data: any, errors: any }>('createDocumentUrl', 'CreateDocumentUrlInput', { deviceId, documentName, contentType }, ['url']);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute createDocument API: id=${reqid}`;
        logger.api.error('Lantana-API', msg, resp.headers);
        throw { deviceId, documentName, err: new Error(msg) };
    }

    logger.api.info('Lantana-API', 'Succeed to request: createDocumentUrl', resp.headers);

    return resp.data.data.createDocumentUrl;
}

/**
 * kintone API をラップしてリクエストを送信する関数。
 *
 * @param {string} type - 実行する kintone API の種類。指定方法はapiのreadme参照 https://ap-northeast-1.console.aws.amazon.com/codesuite/codecommit/repositories/lantana-api/browse/refs/heads/develop/--/Readme.md?region=ap-northeast-1&lines=86-106
 * @param {string} requestParam - API に送信するリクエストパラメータ（JSON 形式の文字列）。指定方法はapiのreadme参照 https://ap-northeast-1.console.aws.amazon.com/codesuite/codecommit/repositories/lantana-api/browse/refs/heads/develop/--/Readme.md?region=ap-northeast-1&lines=86-106
 * @returns {Promise<kintoneResponse>} kintone API のレスポンスを Promise で返す。
 * @throws {Error} API リクエストが失敗した場合にエラーをスローする。
 */
export async function kintoneAPIWrapper(type: string, requestParam: string): Promise<kintoneResponse> {
    const logger = Log.getLogger();
    logger.api.info('Lantana-API', 'Start to request: kintoneAPIWrapper');

    const resp = await mutation<{ data: any, errors: any }>('kintoneRestAPIWrapper', 'KintoneRestAPIWrapperInput', { type, requestParam }, ['kintoneResponse']);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {
        console.error(resp.data.errors)
        const reqid = getEwbReqId(resp);
        const msg = `Failed to execute kintoneAPIWrapper API: id=${reqid}`;
        logger.api.error('Lantana-API', msg, resp.headers);
        throw new Error(msg);
    }

    logger.api.info('Lantana-API', 'Succeed to request: kintoneAPIWrapper', resp.headers);

    return resp.data.data.kintoneRestAPIWrapper;
}

/**
 * 指定されたサービスのトークンの有効期限を取得する。
 * レコードがそもそもない場合はエラーではなく過去の日時を返す
 * @param {string} service - トークンの有効期限を取得する対象のサービス名
 * @returns {Promise<LinkServiceTokenExpiredDate>} - `accessTokenExpiredDate` と `refreshTokenExpiredDate` を含むオブジェクト(日時はISO8601)
 * @throws {Error} - APIリクエストが失敗した場合
 * 
 * @example
 * try {
 *   const tokenData = await linkServiceTokenExpiredDate('myService');
 *   console.log(tokenData.accessTokenExpiredDate, tokenData.refreshTokenExpiredDate);
 * } catch (error) {
 *   console.error(error);
 * }
 */
export async function linkServiceTokenExpiredDate(service: string): Promise<LinkServiceTokenExpiredDate> {
    const logger = Log.getLogger();
    logger.api.info('Lantana-API', 'Start to request: linkServiceTokenExpiredDate');

    const resp = await query<LinkServiceTokenExpiredDate>('linkServiceTokenExpiredDate', 'service', { service: service }, ['accessTokenExpiredDate', 'refreshTokenExpiredDate']);
    if (resp.data.errors && resp.data.errors.length && resp.data.errors.length > 0) {

        //DBにそもそもレコードがない場合は過去の日時を返す
        if (resp.data.errors[0].message === 'A problem occurred while executing linkServiceTokenExpiredDate on type Query. Record is not found') {
            return { accessTokenExpiredDate: '2000-01-01T23:59:59Z', refreshTokenExpiredDate: '2000-01-01T23:59:59Z', };
        }

        const msg = `Failed to linkServiceTokenExpiredDate API: id=${getEwbReqId(resp)}`;
        logger.api.error('Lantana-API', msg, resp.headers);
        throw new Error(msg);
    }

    logger.api.info('Lantana-API', 'Succeed to request: linkServiceTokenExpiredDate', resp.headers);

    return resp.data.data.linkServiceTokenExpiredDate
}

//Query関数のオーバーロード
function query<T>(method: 'linkServiceTokenExpiredDate', inputKey: 'service', input: Input, output: string[]): Promise<Resp_input<typeof method, T>>;
function query<T>(method: 'tenantDevices', output: string[]): Promise<AxiosResponse<T, GraphQLBody>>;
function query<T>(method: 'deviceDisplayNames', output: string[]): Promise<AxiosResponse<T, GraphQLBody>>;

/**
 * LantnaAPIのGraphQLエンドポイントに対してクエリを実行する関数をクエリの引数から分岐させるための関数。
 * 引数が無ければquery_noinputへ、引数があればquery_input関数へ分岐する
 */
async function query<T>(method: string, inputKeyOrOutput: string | string[], input?: Input, output?: string[]): Promise<Resp_input<typeof method, T>> {
    if (Array.isArray(inputKeyOrOutput)) {
        output = inputKeyOrOutput;
        return query_noinput(method, output);
    }

    if (input && Array.isArray(output)) {
        const inputKey = inputKeyOrOutput;
        return query_input(method, inputKey, input, output);
    }

    throw new Error('Invalid arguments');
}

type RespBody<T extends string, U> = { data: { [key in T]: U }, errors: any }
type Resp_input<T extends string, U> = AxiosResponse<RespBody<T, U>, GraphQLBody>

async function query_input<T>(method: string, inputKey: string, input: Input, output: string[]): Promise<Resp_input<typeof method, T>> {
    const logger = Log.getLogger();

    const accessToken = await getAccessToken()
    const accessTokenStr = accessToken.toString()
    logger.api.info('Cognito', 'Succeed to request: getAccessToken');

    const reqBody = `{
         "query": "query ${method}($${inputKey}: String!) { ${method}(${inputKey}: $${inputKey}) {  ${output.join(", ")} } }",
         "variables":${JSON.stringify(input)}
       }`;

    const apiResponse = await axios.post<RespBody<typeof method, T>>(
        GRAPHQL_API_URL,
        reqBody,
        {
            headers: {
                'Content-Type': 'application/graphql',
                'Authorization': `Bearer ${accessTokenStr}`,
            }
        }
    );

    return apiResponse;
}

/**
 * 引数がないクエリをGraphQLエンドポイントに対して実行する関数。
 * @returns {Promise<AxiosResponse<T, GraphQLBody>>} - GraphQLのクエリ結果を含むAxiosのレスポンス
 */
async function query_noinput<T>(method: string, output: string[]): Promise<AxiosResponse<T, GraphQLBody>> {
    const logger = Log.getLogger();

    logger.api.info('Cognito', 'Start to request: currentSession');
    const token = await getAccessToken();
    logger.api.info('Cognito', 'Succeed to request: currentSession');

    const apiResponse: AxiosResponse<T, GraphQLBody> = await axios.post<T>(
        GRAPHQL_API_URL,
        `{ "query": "query { ${method} { ${output.join(', ')} }}" }`,
        {
            headers: {
                'Content-Type': 'application/graphql',
                'Authorization': 'Bearer ' + token,
            }
        }
    );

    return apiResponse;
}
